import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ArrowDown, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIP_PHOTO = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/4mgcbt8b_USS%20Wichita%20Gray.jpg";
const AUTHOR_PORTRAIT = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/tfncxvd5_Don%20Inbody%20Navy.jpg";

// Simple fade-in hook using IntersectionObserver
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("mc-visible"); obs.unobserve(el); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export default function MorseChronicles() {
  const [email, setEmail] = useState("");
  const [subMsg, setSubMsg] = useState("");
  const [subLoading, setSubLoading] = useState(false);

  const synopsisRef = useFadeIn();
  const teaserRef   = useFadeIn();
  const bioRef      = useFadeIn();
  const signupRef   = useFadeIn();
  const pressRef    = useFadeIn();

  useEffect(() => {
    document.title = "The Morse Chronicles — Coming Soon";
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    setSubLoading(true);
    try {
      const res = await axios.post(`${API}/mailing-list`, { email });
      setSubMsg(res.data.message);
      setEmail("");
    } catch {
      setSubMsg("Something went wrong. Please try again.");
    } finally {
      setSubLoading(false);
    }
  };

  const scrollDown = () => {
    document.getElementById("mc-synopsis")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="mc-root">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section data-testid="mc-hero" className="mc-hero">
        {/* Ship background */}
        <div
          className="mc-hero-bg"
          style={{ backgroundImage: `url(${SHIP_PHOTO})` }}
        />
        {/* Gradient overlay — blends ship into dark page */}
        <div className="mc-hero-overlay" />

        {/* Content */}
        <div className="mc-hero-content">
          <p className="mc-overline mc-fade-1">A Novel &bull; Donald Inbody</p>
          <h1 data-testid="mc-title" className="mc-title mc-fade-2">
            The Morse<br />Chronicles
          </h1>
          <div className="mc-rule mc-fade-3" />
          <p data-testid="mc-tagline" className="mc-tagline mc-fade-4">
            An obsolete cruiser chases a raider<br />in the Dutch East Indies.
          </p>
          <p className="mc-coming-soon mc-fade-5">Coming Soon</p>
        </div>

        {/* Scroll indicator */}
        <button
          data-testid="mc-scroll-down"
          onClick={scrollDown}
          className="mc-scroll-btn mc-fade-5"
          aria-label="Scroll down"
        >
          <ArrowDown size={18} />
        </button>
      </section>

      {/* ── Synopsis ──────────────────────────────────────────────────────── */}
      <section id="mc-synopsis" data-testid="mc-synopsis" className="mc-section mc-section-dark">
        <div className="mc-container">
          <div ref={synopsisRef} className="mc-reveal mc-synopsis-grid">
            <div className="mc-synopsis-label">
              <span className="mc-overline">The Story</span>
            </div>
            <div className="mc-synopsis-body">
              <h2 className="mc-h2 mb-8">Deeper than the ocean floor.<br />Faster than a radio signal.</h2>
              <div className="mc-prose">
                <p>
                  1941. As the Imperial Japanese Navy tightens its grip on the Pacific, a worn-out American cruiser — obsolete before the first shot was fired — is ordered to do the impossible: run down a ghost raider preying on Allied shipping across thousands of miles of hostile ocean.
                </p>
                <p>
                  With no air cover, inadequate armament, and a crew stretched to the breaking point, the only advantage remaining is the faint crackle of Morse code — intercepted signals that may be the sole clue to their quarry's next position.
                </p>
                <p>
                  From the wardrooms of the Dutch East Indies to the shattered harbors of a world at war, <em>The Morse Chronicles</em> is a story of signal and silence, duty and sacrifice — and the men who refused to yield.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Teaser Excerpt ────────────────────────────────────────────────── */}
      <section data-testid="mc-excerpt" className="mc-section mc-section-charcoal">
        <div className="mc-container">
          <div ref={teaserRef} className="mc-reveal mc-excerpt-wrapper">
            <div className="mc-intercept-header">
              <span className="mc-mono">INTERCEPT LOG &mdash; EXTRACT</span>
              <span className="mc-mono mc-restricted">RESTRICTED</span>
            </div>
            <blockquote data-testid="mc-excerpt-text" className="mc-excerpt-text">
              <p>
                "The signal came in at 0317, barely above the static — a burst transmission, no more than four seconds. Petty Officer Kwan pressed the headphones hard against his skull and closed his eyes. He had heard a thousand ghost signals in these waters. This one was different. This one was breathing."
              </p>
            </blockquote>
            <p className="mc-excerpt-note mc-mono">— Chapter One</p>
          </div>
        </div>
      </section>

      {/* ── Author Bio ────────────────────────────────────────────────────── */}
      <section data-testid="mc-author" className="mc-section mc-section-dark">
        <div className="mc-container">
          <div ref={bioRef} className="mc-reveal mc-bio-grid">
            <div className="mc-bio-photo-wrap">
              <img
                src={AUTHOR_PORTRAIT}
                alt="Donald Inbody"
                data-testid="mc-author-portrait"
                className="mc-bio-photo"
              />
            </div>
            <div className="mc-bio-text">
              <span className="mc-overline">The Author</span>
              <h2 className="mc-h2 mt-4 mb-6">Donald Inbody</h2>
              <div className="mc-prose">
                <p>
                  A retired Captain in the United States Navy with twenty-eight years of service — including command of USS Duluth (LPD-6) — Donald Inbody brings firsthand knowledge of the sea, the wardroom, and the weight of command to every page.
                </p>
                <p>
                  He is also the author of the acclaimed non-fiction work <em>The Soldier Vote</em> (Palgrave Macmillan) and holds a PhD in Government from the University of Texas at Austin.
                </p>
              </div>
              <a
                href="/"
                data-testid="mc-author-site-link"
                className="mc-link mt-6 inline-block"
              >
                donaldinbody.com &rarr;
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Early Access Signup ───────────────────────────────────────────── */}
      <section data-testid="mc-signup" className="mc-section mc-section-deep">
        <div ref={signupRef} className="mc-reveal mc-container mc-signup-center">
          <span className="mc-overline">Early Access</span>
          <h2 className="mc-h2 mt-4 mb-4">Be first to know.</h2>
          <p className="mc-signup-sub">
            No spam. No noise. One message when the book arrives.
          </p>

          {subMsg ? (
            <p data-testid="mc-signup-success" className="mc-success-msg">{subMsg}</p>
          ) : (
            <form
              onSubmit={handleSignup}
              data-testid="mc-signup-form"
              className="mc-signup-form"
            >
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                data-testid="mc-email-input"
                className="mc-email-input"
              />
              <button
                type="submit"
                disabled={subLoading}
                data-testid="mc-signup-submit"
                className="mc-submit-btn"
              >
                {subLoading ? "..." : "Notify Me"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── Press / Media ─────────────────────────────────────────────────── */}
      <section data-testid="mc-press" className="mc-section mc-section-charcoal mc-press-section">
        <div ref={pressRef} className="mc-reveal mc-container mc-press-inner">
          <div>
            <span className="mc-overline">Press &amp; Media Inquiries</span>
            <p className="mc-press-label">Contact the author directly</p>
          </div>
          <a
            href="mailto:author@inbody.net"
            data-testid="mc-press-email"
            className="mc-press-email"
          >
            <Mail size={16} />
            don@inbody.net
          </a>
        </div>
        <p className="mc-copyright">
          &copy; {new Date().getFullYear()} Donald Inbody. All rights reserved.
        </p>
      </section>

      {/* ── Page-scoped CSS ────────────────────────────────────────────────── */}
      <style>{`
        .mc-root {
          background: #0A1628;
          color: #F5F0E8;
          font-family: 'IBM Plex Sans', sans-serif;
          overflow-x: hidden;
        }

        /* Hero */
        .mc-hero {
          position: relative;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-end;
          overflow: hidden;
        }
        .mc-hero-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          animation: mcZoom 20s ease-out forwards;
          filter: grayscale(20%);
        }
        @keyframes mcZoom {
          from { transform: scale(1.06); }
          to   { transform: scale(1.0); }
        }
        .mc-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            #0A1628 0%,
            rgba(10,22,40,0.82) 50%,
            rgba(5,10,20,0.45) 100%
          );
        }
        .mc-hero-content {
          position: relative;
          z-index: 10;
          max-width: 900px;
          padding: 0 2rem 5rem;
          margin-left: max(2rem, calc(50vw - 600px));
        }
        @media (min-width: 1024px) {
          .mc-hero-content { padding: 0 3rem 6rem; }
        }

        /* Hero animations */
        .mc-fade-1 { animation: mcFadeUp 0.8s ease 0.2s both; }
        .mc-fade-2 { animation: mcFadeUp 0.8s ease 0.5s both; }
        .mc-fade-3 { animation: mcFadeUp 0.6s ease 0.8s both; }
        .mc-fade-4 { animation: mcFadeUp 0.8s ease 1.0s both; }
        .mc-fade-5 { animation: mcFadeUp 0.8s ease 1.3s both; }
        @keyframes mcFadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* Scroll reveal */
        .mc-reveal { opacity: 0; transform: translateY(32px); transition: opacity 0.8s ease, transform 0.8s ease; }
        .mc-reveal.mc-visible { opacity: 1; transform: translateY(0); }

        /* Typography */
        .mc-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(3.5rem, 9vw, 7.5rem);
          font-weight: 900;
          line-height: 0.92;
          letter-spacing: -0.02em;
          color: #F5F0E8;
          margin: 0.5rem 0;
        }
        .mc-h2 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 700;
          line-height: 1.15;
          color: #F5F0E8;
        }
        .mc-tagline {
          font-size: clamp(1rem, 2.2vw, 1.35rem);
          font-weight: 300;
          color: rgba(245,240,232,0.75);
          line-height: 1.6;
          letter-spacing: 0.01em;
          margin: 1.25rem 0 1rem;
        }
        .mc-overline {
          font-size: 0.65rem;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #C5A059;
          display: block;
        }
        .mc-rule {
          width: 3.5rem;
          height: 2px;
          background: #C5A059;
          margin: 1.5rem 0;
        }
        .mc-coming-soon {
          font-size: 0.75rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: rgba(245,240,232,0.35);
          margin-top: 1.5rem;
        }
        .mc-mono {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.7rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(245,240,232,0.4);
        }
        .mc-prose p {
          color: rgba(245,240,232,0.72);
          font-size: 1rem;
          line-height: 1.85;
          margin-bottom: 1.25em;
          font-weight: 300;
        }
        .mc-prose em { font-style: italic; color: rgba(245,240,232,0.9); }

        /* Scroll button */
        .mc-scroll-btn {
          position: absolute;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10;
          color: rgba(197,160,89,0.6);
          background: none;
          border: none;
          cursor: pointer;
          animation: mcBob 2.5s ease-in-out infinite, mcFadeUp 0.8s ease 1.5s both;
          transition: color 0.3s;
        }
        .mc-scroll-btn:hover { color: #C5A059; }
        @keyframes mcBob {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(6px); }
        }

        /* Sections */
        .mc-section { padding: 6rem 0; }
        .mc-section-dark    { background: #0A1628; }
        .mc-section-charcoal { background: #1A1E2E; }
        .mc-section-deep    { background: #060D18; }
        .mc-container { max-width: 1100px; margin: 0 auto; padding: 0 2rem; }
        @media (min-width: 768px) { .mc-container { padding: 0 3rem; } }

        /* Synopsis grid */
        .mc-synopsis-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 768px) {
          .mc-synopsis-grid {
            grid-template-columns: 140px 1fr;
            gap: 4rem;
            align-items: start;
          }
          .mc-synopsis-label { padding-top: 0.6rem; }
        }

        /* Excerpt */
        .mc-excerpt-wrapper {
          border-left: 3px solid #C5A059;
          padding: 2rem 2.5rem;
          background: rgba(197,160,89,0.04);
        }
        .mc-intercept-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.75rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .mc-restricted {
          color: rgba(197,80,60,0.55);
          letter-spacing: 0.18em;
        }
        .mc-excerpt-text {
          margin: 0 0 1.5rem;
        }
        .mc-excerpt-text p {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.1rem, 2.5vw, 1.4rem);
          font-style: italic;
          line-height: 1.75;
          color: rgba(245,240,232,0.88);
        }
        .mc-excerpt-note { margin-top: 1.5rem; }

        /* Author bio */
        .mc-bio-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          align-items: start;
        }
        @media (min-width: 768px) {
          .mc-bio-grid {
            grid-template-columns: 280px 1fr;
            gap: 5rem;
          }
        }
        .mc-bio-photo-wrap { position: relative; }
        .mc-bio-photo {
          width: 100%;
          max-width: 260px;
          aspect-ratio: 3/4;
          object-fit: cover;
          object-position: top;
          filter: grayscale(60%) contrast(1.1);
        }
        .mc-bio-photo-wrap::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: -8px;
          right: 8px;
          top: 8px;
          border: 1px solid rgba(197,160,89,0.3);
          pointer-events: none;
          z-index: -1;
        }
        .mc-link {
          font-size: 0.8rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #C5A059;
          text-decoration: none;
          transition: letter-spacing 0.3s;
        }
        .mc-link:hover { letter-spacing: 0.18em; }

        /* Signup */
        .mc-signup-center { text-align: center; max-width: 520px; }
        .mc-signup-sub {
          color: rgba(245,240,232,0.45);
          font-size: 0.9rem;
          margin-bottom: 2.5rem;
          letter-spacing: 0.03em;
        }
        .mc-signup-form {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        @media (min-width: 480px) {
          .mc-signup-form { flex-direction: row; gap: 0; }
        }
        .mc-email-input {
          flex: 1;
          background: rgba(245,240,232,0.06);
          border: 1px solid rgba(197,160,89,0.25);
          color: #F5F0E8;
          padding: 0.875rem 1.25rem;
          font-size: 0.9rem;
          font-family: 'IBM Plex Sans', sans-serif;
          outline: none;
          transition: border-color 0.3s;
        }
        .mc-email-input::placeholder { color: rgba(245,240,232,0.3); }
        .mc-email-input:focus { border-color: #C5A059; }
        @media (min-width: 480px) {
          .mc-email-input { border-right: none; }
        }
        .mc-submit-btn {
          background: #C5A059;
          color: #0A1628;
          border: 1px solid #C5A059;
          padding: 0.875rem 2rem;
          font-size: 0.85rem;
          font-weight: 700;
          font-family: 'IBM Plex Sans', sans-serif;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.25s, color 0.25s;
        }
        .mc-submit-btn:hover:not(:disabled) {
          background: #D4B577;
        }
        .mc-submit-btn:disabled { opacity: 0.6; cursor: default; }
        .mc-success-msg {
          color: #C5A059;
          font-size: 0.95rem;
          letter-spacing: 0.05em;
          padding: 1rem 0;
        }

        /* Press */
        .mc-press-section { padding-bottom: 3rem; }
        .mc-press-inner {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          align-items: flex-start;
        }
        @media (min-width: 768px) {
          .mc-press-inner {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }
        }
        .mc-press-label {
          color: rgba(245,240,232,0.45);
          font-size: 0.85rem;
          margin-top: 0.35rem;
          letter-spacing: 0.03em;
        }
        .mc-press-email {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          color: #C5A059;
          font-size: 1rem;
          font-weight: 500;
          text-decoration: none;
          letter-spacing: 0.04em;
          transition: opacity 0.2s;
        }
        .mc-press-email:hover { opacity: 0.75; }
        .mc-copyright {
          text-align: center;
          color: rgba(245,240,232,0.2);
          font-size: 0.7rem;
          letter-spacing: 0.08em;
          margin-top: 4rem;
          padding: 0 2rem 1rem;
        }
      `}</style>
    </div>
  );
}
